import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

const POSTS_PER_PAGE = 6;

export default function Blog() {
  const [visiblePosts, setVisiblePosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const loadMoreRef = useRef(null);

  // Load all published posts from Supabase
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data: posts, error } = await supabase
          .from("blog_posts")
          .select("*")
          .eq("published", true)
          .order("created_at", { ascending: false });
        
        if (error) throw error;
        
        setAllPosts(posts || []);
        setVisiblePosts((posts || []).slice(0, POSTS_PER_PAGE));
        setHasMore((posts || []).length > POSTS_PER_PAGE);
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
        setAllPosts([]);
        setVisiblePosts([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPosts();
  }, []);

  // Load more posts
  const loadMorePosts = useCallback(() => {
    if (isLoading || !hasMore) return;
    
    setIsLoading(true);
    
    // Simulate network delay for smooth UX
    setTimeout(() => {
      const nextPage = page + 1;
      const startIndex = page * POSTS_PER_PAGE;
      const endIndex = startIndex + POSTS_PER_PAGE;
      const newPosts = allPosts.slice(startIndex, endIndex);
      
      if (newPosts.length > 0) {
        setVisiblePosts(prev => [...prev, ...newPosts]);
        setPage(nextPage);
        setHasMore(endIndex < allPosts.length);
      } else {
        setHasMore(false);
      }
      
      setIsLoading(false);
    }, 300);
  }, [page, allPosts, isLoading, hasMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMorePosts();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, loadMorePosts]);

  return (
    <div className="min-h-screen px-4 py-8 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-rotten text-[#d0d0d0] mb-4">
          Soundope Blog
        </h1>
        <p className="text-[#808080] max-w-2xl mx-auto">
          News, updates, and insights from the Soundope team
        </p>
      </div>

      {/* Loading State */}
      {isLoading && visiblePosts.length === 0 && (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#808080]" />
        </div>
      )}

      {/* Posts Grid */}
      {visiblePosts.length > 0 ? (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visiblePosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group neuro-base rounded-2xl p-6 hover:scale-[1.02] transition-all duration-300"
              >
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-[#d0d0d0] group-hover:text-[#a0a0a0] transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                </div>
                
                <p className="text-sm text-[#808080] mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-xs text-[#606060]">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {format(new Date(post.created_at), "MMM d, yyyy")}
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          {/* Load More Trigger / Loading State */}
          <div ref={loadMoreRef} className="flex justify-center py-8">
            {isLoading && (
              <div className="flex items-center gap-2 text-[#808080]">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading more posts...</span>
              </div>
            )}
            {!hasMore && visiblePosts.length > 0 && (
              <p className="text-[#606060] text-sm">No more posts to load</p>
            )}
          </div>
        </>
      ) : !isLoading && (
        <div className="text-center py-16">
          <p className="text-[#808080] text-lg mb-4">No blog posts yet</p>
          <p className="text-[#606060] text-sm">Check back soon for updates!</p>
        </div>
      )}
    </div>
  );
}
